import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';
export default function TissArticleSlider({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
  const { t } = useTranslation();
  const cookie = getCookies();

  const [blogGroups, setBlogGroups] = React.useState<any[]>([]);
  const [articles, setArticles] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const initialGroupId = data?.data?.groupId || '';
  const initialLimit = data?.data?.limit ? parseInt(data.data.limit, 10) : 6;
  const hasContent = !!initialGroupId;

  // Edit mode state
  const [isEditing, setIsEditing] = React.useState(!hasContent);

  // Form state
  const [selectedGroup, setSelectedGroup] = React.useState<string>(initialGroupId);
  const [limit, setLimit] = React.useState<number>(initialLimit);
  const [hasChanges, setHasChanges] = React.useState(false);

  // Fetch blog groups (categories)
  const loadBlogGroups = async () => {
    try {
      const res = await axios.get(`${config.url}/v1/dashboard/bloggroup/all`, {
        headers: { Authorization: 'Bearer ' + cookie['token'] },
      });
      setBlogGroups(res.data.message || []);
    } catch (error) {
      console.error("Failed to load blog groups", error);
    }
  };

  // Fetch articles based on group + limit
  const loadArticles = async () => {
    if (!selectedGroup) {
      setArticles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Assuming your API: GET /v1/blog/:groupUrl?limit=5
      const res = await axios.get(`${config.url}/v1/blog/${selectedGroup}`, {
        params: { limit },
      });
      setArticles(res.data.message?.docs || []);
    } catch (error) {
      console.error("Failed to load articles", error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Save configuration
  const saveConfig = async () => {
    if (!data?._id) return;

    setSaving(true);
    try {
      await axios.put(
        `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
        {
          groupId: selectedGroup,
          limit: limit,
        },
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );
      Swal.fire({
        title: t('Saved successfully'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
      });
      setIsEditing(false);
      setHasChanges(false);
      if (reload) reload();
    } catch (error) {
      Swal.fire({
        title: t('Error saving'),
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      Swal.fire({
        title: t('Discard changes?'),
        text: t('You have unsaved changes. Are you sure you want to cancel?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('Yes, discard'),
        cancelButtonText: t('No, keep editing'),
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedGroup(initialGroupId);
          setLimit(initialLimit);
          setIsEditing(false);
          setHasChanges(false);
        }
      });
    } else {
      setIsEditing(false);
    }
  };

  // Track changes
  React.useEffect(() => {
    const dataEqual = selectedGroup === initialGroupId && limit === initialLimit;
    setHasChanges(!dataEqual);
  }, [selectedGroup, limit, initialGroupId, initialLimit]);

  // Effects
  React.useEffect(() => {
    loadBlogGroups();
  }, []);

  React.useEffect(() => {
    if (selectedGroup) {
      loadArticles();
    } else {
      setArticles([]);
      setLoading(false);
    }
  }, [selectedGroup, limit]);

  const responsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  // Placeholder image fallback
  const getThumbnail = (item: any) => {
    if (item?.toimage?.url) {
      return `${config.fronturl}${item.toimage.url}`;
    }
    return '/intro.jpg'; // Make sure this exists in public/
  };

  // Empty state
  if (!hasContent && !isEditing) {
    return (
      <div
        className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
        onClick={() => setIsEditing(true)}
        dir="rtl"
      >
        <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
          {t('Click to add article slider')}
        </span>
        <span className="text-gray-400 text-xs mt-1">
          {t('Create a slider with articles from a blog group')}
        </span>
      </div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
        <div className="p-0.5">
          <div className="space-y-4">
            {/* Configuration Form */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 rounded-lg p-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-gray-800 text-right">{t('Article Slider Configuration')}</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                    {t('Blog Group')}
                  </label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="w-full py-3 px-4 border-2 border-gray-300 rounded-lg text-right bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  >
                    <option value="">{t('Select group...')}</option>
                    {blogGroups.map((group) => (
                      <option key={group._id} value={group.url}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                    {t('Number of Articles')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={limit}
                    onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full py-3 px-4 border-2 border-gray-300 rounded-lg text-right bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  />
                  <p className="text-xs text-gray-500 mt-1.5 text-right">{t('Maximum 20 articles')}</p>
                </div>
              </div>
            </div>

            {/* Preview Section */}
            {selectedGroup && (
              <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 shadow-xl">
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 rounded-lg p-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800 text-right">{t('Live Preview')}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{t('Articles will appear here')}</p>
                    </div>
                  </div>
                  {loading && (
                    <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
                      <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-xs text-blue-700 font-semibold">{t('Loading...')}</span>
                    </div>
                  )}
                </div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block">
                      <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500 mt-3">{t('Loading articles...')}</p>
                  </div>
                ) : articles.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm text-gray-500">{t('No articles found')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-4 border border-gray-100">
                    <Carousel
                      responsive={responsive}
                      className="pb-4"
                      showDots={articles.length > 3}
                      arrows={articles.length > 3}
                    >
                      {articles.map((item: any) => (
                        <div key={item._id} className="px-2">
                          <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 group">
                            <div className="relative overflow-hidden">
                              <img
                                src={getThumbnail(item)}
                                alt={item.name}
                                className="w-full h-48 object-cover bg-gray-100 group-hover:scale-110 transition-transform duration-300"
                                onError={(e) => (e.currentTarget.src = '/intro.jpg')}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </div>
                            <div className="p-4 text-right bg-white">
                              <p className="font-bold text-gray-800  line-clamp-2 text-[10px]">{item.name}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3 justify-end items-center">
            {hasChanges && (
              <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                {t('Unsaved changes')}
              </span>
            )}
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !selectedGroup || !hasChanges}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('Saving...')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('Save')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Preview Mode
  return (
    <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
      <div className="p-0.5 relative">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block">
              <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-sm text-gray-500 mt-4">{t('Loading articles...')}</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">{t('No articles found')}</p>
            </div>
          </div>
        ) : (
          <div className="pb-4">
            <Carousel
              responsive={responsive}
              showDots={articles.length > 3}
              arrows={articles.length > 3}
              className="pb-4"
            >
              {articles.map((item: any) => (
                <div key={item._id} className="px-2">
                  <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group">
                    <div className="relative overflow-hidden">
                      <img
                        src={getThumbnail(item)}
                        alt={item.name}
                        className="w-full h-56 object-cover bg-gray-100 group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => (e.currentTarget.src = '/intro.jpg')}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-4 text-right bg-white">
                      <h4 className="font-bold text-gray-800 line-clamp-2">{item.name}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        )}

        {/* Edit Button - Appears on hover */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <button
            onClick={() => setIsEditing(true)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('Edit Slider')}
          </button>
        </div>
      </div>
    </div>
  );
}